import { useMemo, useState, type ChangeEvent } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  Calendar,
  ClipboardList,
  ArrowLeft,
  FileText,
  Wallet,
} from 'lucide-react'

import { assignmentsApi } from '@/services/assignments'
import { jobsApi } from '@/services/jobs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { DataTablePagination } from '@/components/common/data-table-pagination'
import { Spinner } from '@/components/ui/spinner'
import { getErrorMessage } from '@/lib/api'
import {
  ASSIGNMENT_STATUSES,
  ASSIGNMENT_STATUS_LABELS,
  getAssignmentStatusBadgeClass,
  getAssignmentStatusLabel,
  resolveAssignmentStatus,
  isTerminalAssignmentStatus,
  canDeleteAssignment,
  getAssignmentDeleteBlockedMessage,
  type AssignmentStatus,
} from '@/lib/assignment-status'
import {
  formatAssignmentPayoutDisplay,
  getActualAssignmentPayoutDisplay,
  isCalculatedAssignmentPayType,
  normalizeAssignmentPayType,
} from '@/lib/assignment-pay'
import { formatAssignmentScheduleForLocale, useOrganizationDateLocale } from '@/lib/org-date-locale'
import { WorkerLifecycleNav } from '@/components/workers/worker-lifecycle-nav'
import { AssignmentDeclineChainIndicator } from '@/components/assignments/assignment-decline-chain'
import { JobTitleLink, formatJobTitleWithId } from '@/components/jobs/job-title-link'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'
import { getLocationReturnPath, resolveReturnToPath, withReturnTo } from '@/lib/return-navigation'
import { resolveEventFamilyAssignmentsBasePath } from '@/lib/event-family-assignments-route'
import { cn, formatCurrency } from '@/lib/utils'
import { useDebouncedValue } from '@/lib/use-debounced-value'
import { toast } from 'sonner'

const formatShortId = (value: string | number | undefined | null) => String(value ?? '').slice(0, 8)

const ASSIGNMENT_DELETE_CONFIRMATION_TEXT = 'delete assignment'
type AssignmentSortValue = 'default' | 'recently_changed' | 'recently_added'

const toOptionalNumber = (value?: string | number | null) => {
  if (value === undefined || value === null || value === '') {
    return null
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const DURATION_UNITS: Record<string, { singular: string; plural: string }> = {
  per_day: { singular: 'day', plural: 'days' },
  per_hour: { singular: 'hr', plural: 'hrs' },
  per_job: { singular: 'job', plural: 'jobs' },
  per_week: { singular: 'week', plural: 'weeks' },
  per_month: { singular: 'month', plural: 'months' },
}

const formatEstimatedDuration = (assignment: { estimated_duration?: string | number | null; pay_type?: string | null }) => {
  const duration = toOptionalNumber(assignment.estimated_duration)
  if (duration === null || duration <= 0) {
    return '-'
  }

  const unit = DURATION_UNITS[assignment.pay_type ?? ''] ?? DURATION_UNITS.per_hour
  const displayValue = Number.isInteger(duration) ? String(duration) : duration.toFixed(2).replace(/\.0+$|(?<=\.[0-9]*[1-9])0+$/u, '')
  return `${displayValue} ${duration === 1 ? unit.singular : unit.plural}`
}

type AssignmentRollup = {
  totalHours: number
  totalPay: number
  latestWorkDate?: string
}

const normalizeStatus = (status?: string | null): AssignmentStatus | 'unknown' => {
  return resolveAssignmentStatus(status) ?? 'unknown'
}

type AssignmentsListPageProps = {
  portalMode?: 'job' | 'project' | 'event'
}

export default function AssignmentsListPage({ portalMode: _portalMode = 'job' }: AssignmentsListPageProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const currentReturnPath = getLocationReturnPath(location)
  const organizationDateLocale = useOrganizationDateLocale(serviceInstanceId)
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const kindFilter = _portalMode
  const labels = useWorkspaceLabels()
  const jobFilter = searchParams.get('job') || searchParams.get('job_id') || undefined
  const workerFilter = searchParams.get('worker') || searchParams.get('worker_id') || undefined
  const unassignedFilter = searchParams.get('unassigned') === 'true'
  const isJobFilterLocked = Boolean(jobFilter)
  const showJobColumn = !isJobFilterLocked

  const scopedJobQuery = useQuery({
    queryKey: ['job', 'assignments-list', jobFilter],
    queryFn: () => jobsApi.getJob(jobFilter!),
    enabled: Boolean(jobFilter),
    staleTime: 60_000,
  })

  const scopedJobLabel = jobFilter
    ? formatJobTitleWithId({
        jobId: jobFilter,
        title: scopedJobQuery.data?.title,
        jobRef: scopedJobQuery.data?.job_ref,
        entityLabel: labels.entity,
      })
    : ''
  const eventFamilyAssignmentsBasePath = labels.mode === 'event'
    ? resolveEventFamilyAssignmentsBasePath(location.pathname)
    : '/events/assignments'
  const backPath = resolveReturnToPath(
    location.search,
    jobFilter
      ? withServiceInstanceId(`${labels.basePath}/${jobFilter}`, serviceInstanceId)
      : withServiceInstanceId(labels.mode === 'event' ? eventFamilyAssignmentsBasePath : `${labels.basePath}/assignments`, serviceInstanceId),
  )
  const assignmentCreateBasePath = labels.mode === 'event' ? `${eventFamilyAssignmentsBasePath}/create` : '/assignments/create'
  const assignmentCreatePath = `${assignmentCreateBasePath}${jobFilter ? `?job_id=${encodeURIComponent(jobFilter)}` : ''}`
  const assignmentEditBasePath = labels.mode === 'event' ? eventFamilyAssignmentsBasePath : '/assignments'
  const assignmentDetailsBasePath = labels.mode === 'event' ? eventFamilyAssignmentsBasePath : '/assignments'
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 400)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [sortBy, setSortBy] = useState<AssignmentSortValue>('recently_changed')
  const [deleteAssignmentId, setDeleteAssignmentId] = useState<string | null>(null)
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('')

  const normalizedStatusFilter = statusFilter === 'all' ? undefined : statusFilter

  const buildWorkdaysPath = (assignment: { job_id: string; worker_id?: string | null }) => {
    const params = new URLSearchParams()
    params.set('job', assignment.job_id)
    if (assignment.worker_id) {
      params.set('worker', assignment.worker_id)
    }
    return withServiceInstanceId(`${labels.workDaysBasePath}?${params.toString()}`, serviceInstanceId)
  }

  const buildWorkerPayoutPath = (assignment: { job_id?: string | number | null; worker_id?: string | number | null }) => {
    const params = new URLSearchParams()
    if (assignment.job_id) {
      params.set('job_id', String(assignment.job_id))
    }
    params.set('action', 'record')
    if (assignment.worker_id) {
      params.set('worker_id', String(assignment.worker_id))
    }
    return withServiceInstanceId(`/finance/worker-payments?${params.toString()}`, serviceInstanceId)
  }

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['assignments', serviceInstanceId, kindFilter, page, pageSize, debouncedSearch, normalizedStatusFilter, jobFilter, workerFilter, unassignedFilter, sortBy],
    queryFn: () => assignmentsApi.getAssignments({
      page,
      page_size: pageSize,
      search: debouncedSearch || undefined,
      status: normalizedStatusFilter,
      kind: kindFilter,
      job_id: jobFilter,
      worker_id: workerFilter,
      unassigned: unassignedFilter || undefined,
      ordering: sortBy === 'default' ? undefined : sortBy,
    }),
    placeholderData: keepPreviousData,
  })

  const assignments = data?.results ?? []
  const assignmentIdsKey = useMemo(
    () => assignments.map((assignment) => assignment.id).sort().join('|'),
    [assignments],
  )
  const isSearchPending = search !== debouncedSearch
  const assignmentsReady = !isLoading && !isFetching && !isSearchPending
  const assignmentToDelete = assignments.find((assignment) => assignment.id === deleteAssignmentId) ?? null
  const deleteConfirmationMatches = deleteConfirmationText === ASSIGNMENT_DELETE_CONFIRMATION_TEXT
  const pendingCount = assignments.filter((assignment) => normalizeStatus(assignment.status) === 'pending').length
  const acceptedCount = assignments.filter((assignment) => normalizeStatus(assignment.status) === 'accepted').length
  const rejectedCount = assignments.filter((assignment) => normalizeStatus(assignment.status) === 'rejected').length
  const autoRejectedCount = assignments.filter((assignment) => normalizeStatus(assignment.status) === 'auto_rejected').length
  const withdrawnCount = assignments.filter((assignment) => normalizeStatus(assignment.status) === 'withdrawn').length
  const completedCount = assignments.filter((assignment) => normalizeStatus(assignment.status) === 'completed').length

  const rollupsQuery = useQuery({
    queryKey: ['assignment-rollups', kindFilter, assignmentIdsKey],
    queryFn: async () => {
      if (assignments.length === 0) {
        return new Map<string, AssignmentRollup>()
      }

      // Fetch all work day entries in a single batch request
      const response = await assignmentsApi.getWorkDayEntries({
        kind: kindFilter,
        page: 1,
        page_size: 5000, // Increase page size to capture all entries for the assignment page
      })

      // Build a map of (job, worker) -> assignment for O(1) lookup
      const assignmentByJobWorker = new Map<string, typeof assignments[0]>()
      assignments.forEach((assignment) => {
        const key = `${assignment.job_id}:${assignment.worker_id || ''}`
        assignmentByJobWorker.set(key, assignment)
      })

      // Group entries by assignment and calculate rollups
      const rollupsByAssignmentId = new Map<string, AssignmentRollup>()

      response.results.forEach((entry) => {
        const key = `${String(entry.job)}:${String(entry.worker) || ''}`
        const assignment = assignmentByJobWorker.get(key)

        if (!assignment) return

        const existing = rollupsByAssignmentId.get(assignment.id) || {
          totalHours: 0,
          totalPay: 0,
          latestWorkDate: undefined,
        }

        const hours = toOptionalNumber(entry.hours) ?? 0
        const pay = toOptionalNumber(entry.total_pay) ?? 0
        const workDate = entry.work_date || ''

        existing.totalHours += hours
        existing.totalPay += pay

        // Update latest work date if this entry's date is more recent
        if (workDate && (!existing.latestWorkDate || workDate > existing.latestWorkDate)) {
          existing.latestWorkDate = workDate
        }

        rollupsByAssignmentId.set(assignment.id, existing)
      })

      // Ensure all assignments have an entry (even if 0 hours/pay)
      assignments.forEach((assignment) => {
        if (!rollupsByAssignmentId.has(assignment.id)) {
          rollupsByAssignmentId.set(assignment.id, {
            totalHours: 0,
            totalPay: 0,
            latestWorkDate: undefined,
          })
        }
      })

      return rollupsByAssignmentId
    },
    enabled: assignments.length > 0 && assignmentsReady,
    staleTime: 5 * 60 * 1000,
  })

  const deleteMutation = useMutation({
    mutationFn: (assignmentId: string) => assignmentsApi.deleteAssignment(assignmentId),
    onSuccess: () => {
      toast.success('Assignment deleted successfully')
      if (assignmentToDelete?.job_id) {
        queryClient.invalidateQueries({ queryKey: ['job-assignments', assignmentToDelete.job_id] })
      }
      if (assignmentToDelete?.worker_id) {
        queryClient.invalidateQueries({ queryKey: ['worker-assignments', assignmentToDelete.worker_id] })
      }
      queryClient.invalidateQueries({ queryKey: ['assignments'] })
      setDeleteAssignmentId(null)
      setDeleteConfirmationText('')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const handleStatusFilterClick = (nextStatus: string) => {
    setStatusFilter((current) => (current === nextStatus ? 'all' : nextStatus))
    setPage(1)
  }

  const renderStatusFilterBadge = (
    filterValue: string,
    label: string,
    count: number,
    badgeClass: string,
  ) => {
    const isActive = statusFilter === filterValue

    return (
      <button
        type="button"
        aria-pressed={isActive}
        title={isActive ? `Clear ${label.toLowerCase()} filter` : `Filter by ${label.toLowerCase()}`}
        onClick={() => handleStatusFilterClick(filterValue)}
        className="inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
      >
        <Badge
          variant="outline"
          className={cn(
            'rounded-md transition-all hover:shadow-sm',
            badgeClass,
            isActive ? 'ring-2 ring-primary/40 ring-offset-1' : 'hover:brightness-95',
          )}
        >
          {label} {count}
        </Badge>
      </button>
    )
  }

  const getStatusBadge = (status?: string | null, options?: { clickable?: boolean }) => {
    const normalizedStatus = normalizeStatus(status)
    const filterValue = normalizedStatus === 'unknown' ? null : normalizedStatus
    const isActive = filterValue !== null && statusFilter === filterValue
    const badgeClass = normalizedStatus === 'unknown'
      ? 'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200'
      : getAssignmentStatusBadgeClass(normalizedStatus)

    const label = normalizedStatus === 'unknown' ? 'Unknown' : getAssignmentStatusLabel(normalizedStatus)
    const sharedClassName = cn(
      badgeClass,
      options?.clickable !== false && filterValue
        ? 'cursor-pointer transition-all hover:shadow-sm hover:brightness-95'
        : '',
      isActive ? 'ring-2 ring-primary/40 ring-offset-1' : '',
    )

    if (options?.clickable !== false && filterValue) {
      return (
        <button
          type="button"
          aria-pressed={isActive}
          title={isActive ? `Clear ${label.toLowerCase()} filter` : `Filter by ${label.toLowerCase()}`}
          onClick={() => handleStatusFilterClick(filterValue)}
          className="inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
        >
          <Badge variant="outline" className={sharedClassName}>
            {label}
          </Badge>
        </button>
      )
    }

    return (
      <Badge variant="outline" className={sharedClassName}>
        {label}
      </Badge>
    )
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center rounded-[28px] border border-destructive/20 bg-card/80 shadow-sm">
        <div className="text-center">
          <p className="mb-2 text-destructive">Failed to load assignments</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-2">
      <WorkerLifecycleNav activeStage="assignments" />

      <AlertDialog open={Boolean(deleteAssignmentId)} onOpenChange={(open) => {
        if (!open && !deleteMutation.isPending) {
          setDeleteAssignmentId(null)
          setDeleteConfirmationText('')
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete assignment?</AlertDialogTitle>
            <AlertDialogDescription>
              {assignmentToDelete
                ? `This will remove ${assignmentToDelete.worker_name || 'the worker'} from ${formatJobTitleWithId({
                    jobId: assignmentToDelete.job_id,
                    title: assignmentToDelete.job_title,
                    jobRef: assignmentToDelete.job_ref,
                    entityLabel: labels.entity,
                  })}.`
                : 'This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Input
              value={deleteConfirmationText}
              onChange={(event) => setDeleteConfirmationText(event.target.value)}
              placeholder="Type delete assignment"
              autoComplete="off"
              disabled={deleteMutation.isPending}
            />
            <p className="text-xs text-muted-foreground">Confirmation is case-sensitive and must exactly match: {ASSIGNMENT_DELETE_CONFIRMATION_TEXT}</p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              disabled={
                deleteMutation.isPending
                || !deleteAssignmentId
                || !canDeleteAssignment(assignmentToDelete)
                || !deleteConfirmationMatches
              }
              onClick={(event) => {
                event.preventDefault()
                if (deleteAssignmentId && canDeleteAssignment(assignmentToDelete)) {
                  deleteMutation.mutate(deleteAssignmentId)
                }
              }}
            >
              {deleteMutation.isPending ? (
                <>
                  <Spinner className="mr-2" />
                  Deleting...
                </>
              ) : 'Delete Assignment'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <section className="flex flex-col gap-3 rounded-2xl border border-border/60 bg-card px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex items-start gap-3">
          {jobFilter && (
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(backPath)}
              title="Back"
              className="mt-0.5 h-10 w-10 shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight text-foreground">Assignments</h1>
            {isJobFilterLocked ? (
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <p className="text-sm text-muted-foreground">
                  Showing assignments for {scopedJobLabel}.
                </p>
                <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">
                  {labels.entity} locked
                </Badge>
              </div>
            ) : workerFilter ? (
              <p className="mt-1 text-sm text-muted-foreground">
                Showing worker #{formatShortId(workerFilter)} assignments.
              </p>
            ) : unassignedFilter ? (
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <p className="text-sm text-muted-foreground">Showing unassigned slots only.</p>
                <Badge variant="outline" className="border-orange-200 bg-orange-50 text-orange-700">
                  Unassigned filter active
                </Badge>
                <Link
                  to={withServiceInstanceId('/assignments', serviceInstanceId)}
                  className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                >
                  Clear filter
                </Link>
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button className="h-10 rounded-lg px-4" asChild>
            <Link
              to={withReturnTo(
                withServiceInstanceId(
                  assignmentCreatePath,
                  serviceInstanceId,
                ),
                currentReturnPath,
              )}
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Assignment
            </Link>
          </Button>
          <Button className="h-10 rounded-lg px-4" variant="outline" asChild>
            <Link to={withServiceInstanceId('/vehicles', serviceInstanceId)}>
              Add Vehicle
            </Link>
          </Button>
          {workerFilter && !isJobFilterLocked ? (
            <Button className="h-10 rounded-lg px-4" variant="outline" asChild>
              <Link to={withServiceInstanceId('/assignments', serviceInstanceId)}>
                View All
              </Link>
            </Button>
          ) : null}
        </div>
      </section>

      <Card className="rounded-2xl border-border/60 bg-card shadow-sm">
        <CardHeader className="gap-3 border-b border-border/60 pb-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <CardTitle className="text-lg font-semibold">Assignment List</CardTitle>
              <div className="mt-2 flex flex-wrap gap-2">
                {renderStatusFilterBadge(
                  'all',
                  'Total',
                  data?.count ?? 0,
                  'border-slate-200 bg-slate-100 text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100',
                )}
                {renderStatusFilterBadge('pending', 'Pending', pendingCount, getAssignmentStatusBadgeClass('pending'))}
                {renderStatusFilterBadge('accepted', 'Accepted', acceptedCount, getAssignmentStatusBadgeClass('accepted'))}
                {renderStatusFilterBadge('rejected', 'Rejected', rejectedCount, getAssignmentStatusBadgeClass('rejected'))}
                {renderStatusFilterBadge('auto_rejected', 'Auto Rejected', autoRejectedCount, getAssignmentStatusBadgeClass('auto_rejected'))}
                {renderStatusFilterBadge('withdrawn', 'Withdrawn', withdrawnCount, getAssignmentStatusBadgeClass('withdrawn'))}
                {renderStatusFilterBadge('completed', 'Completed', completedCount, getAssignmentStatusBadgeClass('completed'))}
              </div>
            </div>

            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <div className="relative min-w-[280px]">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by job, worker, or assignment"
                  value={search}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setSearch(event.target.value)
                    setPage(1)
                  }}
                  className="h-10 rounded-lg border-border/70 bg-muted/30 pl-9 shadow-none"
                />
              </div>

              <Select
                value={statusFilter}
                onValueChange={(value: string) => {
                  setStatusFilter(value)
                  setPage(1)
                }}
              >
                <SelectTrigger className="h-10 w-full rounded-lg border-border/70 bg-muted/30 sm:w-[190px]">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {ASSIGNMENT_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>
                      {ASSIGNMENT_STATUS_LABELS[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={sortBy}
                onValueChange={(value: AssignmentSortValue) => {
                  setSortBy(value)
                  setPage(1)
                }}
              >
                <SelectTrigger className="h-10 w-full rounded-lg border-border/70 bg-muted/30 sm:w-[210px]">
                  <SelectValue placeholder="Sort assignments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recently_changed">Recently changed</SelectItem>
                  <SelectItem value="recently_added">Recently added</SelectItem>
                  <SelectItem value="default">{labels.entity} schedule</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant={unassignedFilter ? 'default' : 'outline'}
                size="sm"
                className="h-10 shrink-0 rounded-lg px-3 text-sm"
                onClick={() => {
                  const next = new URLSearchParams(location.search)
                  if (unassignedFilter) {
                    next.delete('unassigned')
                  } else {
                    next.set('unassigned', 'true')
                  }
                  next.delete('page')
                  navigate({ search: next.toString() })
                }}
              >
                Unassigned only
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-3 sm:p-4">
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="rounded-2xl border border-border/60 bg-muted/30 p-4">
                  <Skeleton className="h-12 w-full rounded-xl" />
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="overflow-hidden rounded-xl border border-border/70 bg-background">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/35 hover:bg-muted/35">
                      <TableHead className="h-11 pl-4 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Assignment</TableHead>
                      {showJobColumn ? (
                        <TableHead className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">{labels.entity}</TableHead>
                      ) : null}
                      <TableHead className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Role</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Worker</TableHead>
                      <TableHead className="w-[14rem] text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Dates</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Duration</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Actual Payout</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Status</TableHead>
                      <TableHead className="w-12 pr-4" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assignments.length > 0 ? (
                      assignments.map((assignment) => {
                        const assignmentPayType = normalizeAssignmentPayType(assignment)
                        const usesCalculatedPayout = isCalculatedAssignmentPayType(assignmentPayType)
                        const actualPayoutDisplay = getActualAssignmentPayoutDisplay(assignment)
                        const assignmentCanDelete = canDeleteAssignment(assignment)

                        return (
                        <TableRow key={assignment.id} className="border-border/60 transition-colors hover:bg-muted/25">
                          <TableCell className="pl-4 align-top">
                            <div className="space-y-1">
                              <Link
                                to={withReturnTo(
                                  withServiceInstanceId(
                                    `${assignmentDetailsBasePath}/${assignment.id}/details`,
                                    serviceInstanceId,
                                  ),
                                  currentReturnPath,
                                )}
                                className="font-medium text-foreground transition-colors hover:text-primary"
                              >
                                #{formatShortId(assignment.id)}
                              </Link>
                              {assignment.assignment_ref ? (
                                <p className="text-xs text-muted-foreground">{assignment.assignment_ref}</p>
                              ) : null}
                              <AssignmentDeclineChainIndicator
                                assignment={assignment}
                                serviceInstanceId={serviceInstanceId}
                                returnPath={currentReturnPath}
                                detailBasePath={assignmentDetailsBasePath}
                              />
                            </div>
                          </TableCell>
                          {showJobColumn ? (
                            <TableCell>
                              <JobTitleLink
                                jobId={assignment.job_id}
                                title={assignment.job_title}
                                jobRef={assignment.job_ref}
                                serviceInstanceId={serviceInstanceId}
                                returnPath={currentReturnPath}
                                className="transition-colors hover:text-primary/80"
                              />
                            </TableCell>
                          ) : null}
                          <TableCell className="text-sm text-muted-foreground">
                            {assignment.role_label || assignment.planner_resource_label || '-'}
                          </TableCell>
                          <TableCell>
                            {assignment.worker_id ? (
                              <Link
                                to={withServiceInstanceId(`/workers/${assignment.worker_id}`, serviceInstanceId)}
                                className="text-primary transition-colors hover:text-primary/80 hover:underline"
                              >
                                {assignment.worker_name || formatShortId(assignment.worker_id)}
                              </Link>
                            ) : (
                              <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                                Unassigned
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="align-top">
                            <div className="flex max-w-[14rem] items-start gap-2 text-sm text-muted-foreground">
                              <Calendar className="h-4 w-4" />
                              <span className="whitespace-normal break-words leading-5">
                                {formatAssignmentScheduleForLocale(assignment, organizationDateLocale)}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatEstimatedDuration(assignment)}
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1 text-sm">
                              <p className="font-medium text-foreground">
                                {!assignmentsReady || (usesCalculatedPayout && rollupsQuery.isLoading)
                                  ? 'Calculating...'
                                  : formatAssignmentPayoutDisplay(actualPayoutDisplay, formatCurrency)}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="align-top">{getStatusBadge(assignment.status, { clickable: true })}</TableCell>
                          <TableCell className="pr-4">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl border border-transparent hover:border-border/70 hover:bg-muted/60">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-44 rounded-xl">
                                <DropdownMenuItem asChild>
                                  <Link
                                    to={withServiceInstanceId(
                                      `${assignmentDetailsBasePath}/${assignment.id}/details${location.search || ''}`,
                                      serviceInstanceId
                                    )}
                                  >
                                    <FileText className="mr-2 h-4 w-4" />
                                    Details
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                  <Link
                                    to={withReturnTo(withServiceInstanceId(
                                      `${assignmentDetailsBasePath}/${assignment.id}/details`,
                                      serviceInstanceId
                                    ), currentReturnPath)}
                                  >
                                    <Eye className="mr-2 h-4 w-4" />
                                    View Assignment
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild={!isTerminalAssignmentStatus(assignment.status)} disabled={isTerminalAssignmentStatus(assignment.status)}>
                                  {isTerminalAssignmentStatus(assignment.status) ? (
                                    <>
                                      <Edit className="mr-2 h-4 w-4" />
                                      Edit Assignment
                                    </>
                                  ) : (
                                    <Link
                                      to={withReturnTo(withServiceInstanceId(
                                        `${assignmentEditBasePath}/${assignment.id}/edit${location.search || ''}`,
                                        serviceInstanceId
                                      ), currentReturnPath)}
                                    >
                                      <Edit className="mr-2 h-4 w-4" />
                                      Edit Assignment
                                    </Link>
                                  )}
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild={!isTerminalAssignmentStatus(assignment.status)} disabled={isTerminalAssignmentStatus(assignment.status)}>
                                  {isTerminalAssignmentStatus(assignment.status) ? (
                                    <>
                                      <Calendar className="mr-2 h-4 w-4" />
                                      Work Days
                                    </>
                                  ) : (
                                    <Link to={buildWorkdaysPath({
                                      job_id: assignment.job_id,
                                      worker_id: assignment.worker_id,
                                    })}>
                                      <Calendar className="mr-2 h-4 w-4" />
                                      Work Days
                                    </Link>
                                  )}
                                </DropdownMenuItem>
                                {assignment.worker_id ? (
                                  <DropdownMenuItem asChild>
                                    <Link to={buildWorkerPayoutPath({
                                      job_id: assignment.job_id,
                                      worker_id: assignment.worker_id,
                                    })}>
                                      <Wallet className="mr-2 h-4 w-4" />
                                      Record payout
                                    </Link>
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem disabled>
                                    <Wallet className="mr-2 h-4 w-4" />
                                    Record payout
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  disabled={!assignmentCanDelete}
                                  onClick={() => setDeleteAssignmentId(assignment.id)}
                                  title={assignmentCanDelete ? undefined : getAssignmentDeleteBlockedMessage()}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      )})
                    ) : (
                      <TableRow>
                        <TableCell colSpan={showJobColumn ? 9 : 8} className="h-40">
                          <div className="flex flex-col items-center justify-center gap-3 text-center text-muted-foreground">
                            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/60 text-foreground">
                              <ClipboardList className="h-6 w-6" />
                            </div>
                            <div>
                              <p className="font-medium text-foreground">No assignments found</p>
                              <p className="mt-1 text-sm text-muted-foreground">Refine your filters or create a new assignment to get started.</p>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {data && (
                <div className="mt-3 rounded-xl border border-border/60 bg-muted/20 px-3 py-2">
                  <DataTablePagination
                    page={page}
                    pageSize={pageSize}
                    totalCount={data.count}
                    onPageChange={setPage}
                    onPageSizeChange={(size: number) => {
                      setPageSize(size)
                      setPage(1)
                    }}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
